import { SimpleTable, Text } from '@pandiafi/components'
import classNames from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { PaymentsByMonth } from '../../../../common/generated/residentApiSchemas'
import { toShortDate, localizeCurrency, sumTotal } from '../utils'
import styles from './PaymentTable.module.css'

interface TargetTableProps {
  paymentsByTarget: PaymentsByMonth['paymentsByTarget']
  openCreditNotes: PaymentsByMonth['openCreditNotes']
}

type CreditNoteRow = {
  key: string
  label: string
  date: string
  amount: string
}

const toNegativeAmount = (amount: string) =>
  (-Math.abs(Number(amount))).toFixed(2)

const toCreditNoteRows = (
  paymentsByTarget: PaymentsByMonth['paymentsByTarget'],
  openCreditNotes: PaymentsByMonth['openCreditNotes'],
  creditNoteLabel: string
): CreditNoteRow[] => {
  const appliedRows: CreditNoteRow[] = paymentsByTarget.flatMap((target) =>
    target.appliedCreditNotes.map((cn, appliedIndex) => ({
      key: `applied-${target.targetId}-${appliedIndex}`,
      label: cn.message
        ? `${creditNoteLabel} (${cn.message})`
        : creditNoteLabel,
      date: cn.date,
      amount: toNegativeAmount(cn.amount),
    }))
  )

  const openRows: CreditNoteRow[] = openCreditNotes.map((cn, openIndex) => ({
    key: `open-${openIndex}`,
    label: cn.message ? `${cn.nameOnBill} (${cn.message})` : cn.nameOnBill,
    date: cn.date,
    amount: toNegativeAmount(cn.amount),
  }))

  return [...appliedRows, ...openRows].sort((a, b) =>
    a.date.localeCompare(b.date)
  )
}

export const TargetTable: React.FC<TargetTableProps> = ({
  paymentsByTarget,
  openCreditNotes,
}) => {
  const { t } = useTranslation(['payments'])

  if (paymentsByTarget.length === 0 && openCreditNotes.length === 0)
    return <Text element="p">{t('payments:noTargets')}</Text>

  const creditNoteRows = toCreditNoteRows(
    paymentsByTarget,
    openCreditNotes,
    t('payments:creditNote')
  )
  const sums = [
    ...paymentsByTarget.map((target) => target.expected),
    ...openCreditNotes.map((creditNote) => toNegativeAmount(creditNote.amount)),
  ]

  return (
    <SimpleTable
      className={styles.table}
      header={
        <tr>
          <th>{t('payments:itemisation')}</th>
          <th className={styles.number}>{t('payments:dueDate')}</th>
          <th className={styles.number}>{t('payments:sum')}</th>
        </tr>
      }
      footer={
        <tr>
          <td colSpan={2} className={styles.total}>
            {t('payments:sumTotal')}
          </td>
          <td className={classNames(styles.number, styles.total)}>
            {localizeCurrency(sumTotal(sums.map((s) => Number(s))))}
          </td>
        </tr>
      }
    >
      {paymentsByTarget.map((payment) => (
        <tr key={payment.targetId}>
          <td>{payment.nameOnBill}</td>
          <td className={styles.number}>{toShortDate(payment.dueDate)}</td>
          <td className={styles.number}>
            {payment.appliedCreditNotes.length > 0
              ? `${localizeCurrency(Number(payment.expected))} (${localizeCurrency(
                  Number(payment.originalValue)
                )})`
              : localizeCurrency(Number(payment.expected))}
          </td>
        </tr>
      ))}

      {creditNoteRows.map((row) => (
        <tr key={row.key}>
          <td>{row.label}</td>
          <td className={styles.number}>{toShortDate(row.date)}</td>
          <td className={styles.number}>
            {localizeCurrency(Number(row.amount))}
          </td>
        </tr>
      ))}
    </SimpleTable>
  )
}
